// components/Recordings/RecordingsListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Video,
  Play,
  Calendar,
  Film,
  Clock,
  CheckCircle2,
  MoreVertical,
  GraduationCap,
  MonitorPlay,
} from "lucide-react";
import {
  PageHeader,
  EmptyState,
  SearchInput,
  SelectControl,
  Paginator,
  ActionMenu,
  ActionMenuItem,
} from "../ui";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import "./Recordings.scss";

const PAGE_SIZE = 8;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * ProgressRing — compact SVG completion ring.
 * `state` controls the stroke colour: done (green) / progress (blue) / todo (grey).
 */
const ProgressRing = ({ value = 0, state = "todo" }) => {
  const R = 26;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - Math.min(Math.max(value, 0), 100) / 100);
  return (
    <div className="learn-ring" aria-label={`${value}% complete`}>
      <svg viewBox="0 0 64 64" className="learn-ring__svg">
        <circle cx="32" cy="32" r={R} className="learn-ring__track" />
        <circle
          cx="32"
          cy="32"
          r={R}
          className={`learn-ring__fill learn-ring__fill--${state}`}
          style={{
            strokeDasharray: C,
            strokeDashoffset: offset,
            transform: "rotate(-90deg)",
            // Rotate around the SVG-space centre (matches cx/cy). Do NOT use
            // transform-box: fill-box here — that re-bases the origin to the
            // circle's bbox corner and skews the arc start point.
            transformOrigin: "32px 32px",
          }}
        />
      </svg>
      <span className="learn-ring__num">{value}%</span>
    </div>
  );
};

// Derive a 3-state completion bucket from a view-status entry.
const deriveProgress = (vs) => {
  const percent = vs?.percent ?? (vs?.viewed ? 100 : 0);
  const state = percent >= 100 ? "done" : percent > 0 ? "progress" : "todo";
  return { percent, state };
};

const RecordingsListPage = ({ batchId, embedded = false }) => {
  const [recordings, setRecordings] = useState([]);
  const [courseThumbnailUrl, setCourseThumbnailUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewStatus, setViewStatus] = useState({});

  const [statusFilter, setStatusFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("recent");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecordings = async () => {
      setLoading(true);
      try {
        const params = batchId ? `?batchId=${batchId}` : "";
        const { data } = await apiClient.get(`/videos/student/my-videos${params}`);
        const vids = data?.data || [];
        setRecordings(vids);
        setCourseThumbnailUrl(data?.courseThumbnailUrl || "");

        const resolvedBatchId = batchId || vids[0]?.batch?._id || vids[0]?.batch;
        if (resolvedBatchId) {
          try {
            const vsRes = await apiClient.get(`/videos/batch/${resolvedBatchId}/view-status`);
            setViewStatus(vsRes.data?.data || {});
          } catch {}
        }
      } catch {
        setRecordings([]);
      }
      setLoading(false);
    };
    fetchRecordings();
  }, [batchId]);

  // Reset to first page whenever the filter set changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sessionFilter, sortOrder, search]);

  // "All Sessions" dropdown options — distinct session topics from the recordings.
  const sessionOptions = useMemo(() => {
    const seen = new Map();
    for (const r of recordings) {
      const t = (r.topic || r.title || "").trim();
      if (t && !seen.has(t)) seen.set(t, t);
    }
    return [
      { value: "all", label: "All Sessions" },
      ...Array.from(seen.values()).map((t) => ({ value: t, label: t })),
    ];
  }, [recordings]);

  const markWatched = async (id) => {
    // optimistic
    setViewStatus((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), viewed: true },
    }));
    try {
      await apiClient.post(`/videos/${id}/mark-viewed`);
    } catch {
      // revert on failure
      setViewStatus((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), viewed: false },
      }));
    }
  };

  const filtered = useMemo(() => {
    let list = [...recordings];
    list.sort((a, b) => {
      const da = new Date(a.recordedAt).getTime();
      const db = new Date(b.recordedAt).getTime();
      return sortOrder === "recent" ? db - da : da - db;
    });
    // Sequence number follows the sorted order (newest = 1 in "recent")
    list = list.map((r, i) => ({ ...r, _seq: i + 1 }));

    if (statusFilter !== "all") {
      list = list.filter((r) => {
        const { state } = deriveProgress(viewStatus[r._id]);
        return state === statusFilter;
      });
    }

    if (sessionFilter !== "all") {
      list = list.filter((r) => (r.topic || r.title || "").trim() === sessionFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        `${r.topic || ""} ${r.title || ""} ${r.description || ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    return list;
  }, [recordings, sortOrder, statusFilter, sessionFilter, search, viewStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Loading ──
  if (loading) {
    return (
      <div className="recordings-page">
        {!embedded && <Shimmer height={48} count={1} />}
        <div className="learn-list mt-3">
          {[...Array(5)].map((_, i) => (
            <Shimmer key={i} height={120} />
          ))}
        </div>
      </div>
    );
  }

  // ── Hard empty (no recordings at all) ──
  if (recordings.length === 0) {
    return (
      <div className="recordings-page">
        {!embedded && (
          <PageHeader
            title="Class Recordings"
            description="Watch your class recordings and review past sessions"
            icon={Video}
            backButton
          />
        )}
        <EmptyState
          icon={<Video size={40} />}
          title="No recordings yet"
          description="Class recordings will appear here once your instructor records a session."
        />
      </div>
    );
  }

  return (
    <div className="recordings-page">
      {!embedded && (
        <PageHeader
          title="Class Recordings"
          description="Watch your class recordings and review past sessions"
          icon={Video}
          backButton
        />
      )}

      {/* ── Toolbar ── */}
      <div className="learn-toolbar">
        <div className="learn-toolbar__filters">
          <SelectControl
            value={sessionFilter}
            onChange={setSessionFilter}
            ariaLabel="Filter by session"
            options={sessionOptions}
          />
          <SelectControl
            value={statusFilter}
            onChange={setStatusFilter}
            ariaLabel="Filter by status"
            options={[
              { value: "all", label: "All Status" },
              { value: "done", label: "Completed" },
              { value: "progress", label: "In Progress" },
              { value: "todo", label: "Not Started" },
            ]}
          />
        </div>
        <div className="learn-toolbar__right">
          <SelectControl
            value={sortOrder}
            onChange={setSortOrder}
            ariaLabel="Sort order"
            options={[
              { value: "recent", label: "Recent First" },
              { value: "oldest", label: "Oldest First" },
            ]}
          />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search sessions…"
            className="learn-toolbar__search"
          />
        </div>
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <div className="learn-noresults">
          <MonitorPlay size={28} />
          <p>No recordings match your filters.</p>
        </div>
      ) : (
        <div className="learn-list">
          {pageItems.map((rec) => {
            const vs = viewStatus[rec._id];
            const watched = !!vs?.viewed;
            const { percent, state } = deriveProgress(vs);
            const title = rec.topic || rec.title || "Recording";
            const thumb = courseThumbnailUrl || rec.thumbnailUrl;
            const dur = formatDuration(rec.duration);
            const instructor = rec.uploadedBy?.name;

            return (
              <div
                key={rec._id}
                className={`learn-row learn-row--${state}`}
                onClick={() => navigate(`/s/recordings/${rec._id}`)}
              >
                {/* Thumbnail */}
                <div className="learn-row__thumb">
                  {thumb ? (
                    <img src={thumb} alt={title} loading="lazy" />
                  ) : (
                    <div className="learn-row__thumb-ph">
                      <Film />
                    </div>
                  )}
                  <div className="learn-row__play">
                    <Play />
                  </div>
                  {dur && (
                    <span className="learn-row__dur">
                      <Clock size={10} />
                      {dur}
                    </span>
                  )}
                </div>

                {/* Main */}
                <div className="learn-row__main">
                  <div className="learn-row__top">
                    <span className="learn-row__seq">{rec._seq}</span>
                    <h4 className="learn-row__title">{title}</h4>
                  </div>

                  <div className="learn-row__meta">
                    <span>
                      <Calendar size={12} />
                      {formatDate(rec.recordedAt)}
                    </span>
                    <span>
                      <Video size={12} />
                      Class
                    </span>
                    {instructor && (
                      <span>
                        <GraduationCap size={12} />
                        By {instructor}
                      </span>
                    )}
                  </div>

                  {rec.description && (
                    <p className="learn-row__desc">{rec.description}</p>
                  )}
                </div>

                {/* Aside: [ status + ring stacked ] [ action menu ] */}
                <div className="learn-row__aside">
                  <div className="learn-row__aside-col">
                    <span className={`learn-row__status learn-row__status--${state}`}>
                      {state === "done" ? (
                        <>
                          <CheckCircle2 size={13} /> Completed
                        </>
                      ) : state === "progress" ? (
                        <>
                          <Play size={13} /> In Progress
                        </>
                      ) : (
                        <>
                          <Play size={13} /> Not Started
                        </>
                      )}
                    </span>

                    <div className="learn-row__progress">
                      <ProgressRing value={percent} state={state} />
                    </div>
                  </div>

                  <div
                    className="learn-row__menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionMenu trigger={<MoreVertical size={16} />}>
                      <ActionMenuItem
                        onClick={() => navigate(`/s/recordings/${rec._id}`)}
                      >
                        <Play size={14} /> Watch now
                      </ActionMenuItem>
                      {!watched && (
                        <ActionMenuItem onClick={() => markWatched(rec._id)}>
                          <CheckCircle2 size={14} /> Mark as watched
                        </ActionMenuItem>
                      )}
                    </ActionMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {filtered.length > 0 && (
        <Paginator
          page={page}
          totalPages={totalPages}
          total={filtered.length}
          pageSize={PAGE_SIZE}
          onChange={setPage}
          infoLabel="recordings"
          className="learn-paginator"
        />
      )}
    </div>
  );
};

export default RecordingsListPage;
